import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  AnnouncementSlugs,
  CardTypeCallToActions,
  FilterMatchTitle,
  FilterMatchType,
  Learning_Type,
  Product,
  RouteTypes,
  SessionStorageKeys,
} from '@app/enums/_index';
import {
  CMSSubscriptionInterface,
  HasAnalytics,
  HasPageTitleAndMetaData,
  StaticPageInterface,
} from '@app/interfaces/_index';
import {
  FilterCategoryItem,
  LMSSubscription,
  SearchObject,
  StaticPage,
  User,
} from '@app/models/_index';
import {
  AnnouncementService,
  EnrollmentService,
  GoogleAnalyticsService,
  SearchService,
  SubscriptionService,
  UserService,
  WordpressSubscriptionsService,
} from '@app/services/_index';
import {
  compareSwap,
  getExpirationTime,
  lockBackgroundScrolling,
  logGoogleAnalytics,
  truncate,
} from '@app/utilities/_index';

import { Location } from '@angular/common';
import { StaticPageAdapter, SubscriptionAdapter } from '@app/adapters/_index';
import { PagingComponent } from '@app/components/_index';
import { FilterCategoryFactory } from '@app/factories/_index';
import { environment } from '@environments/environment';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  standalone: false,
})
export class SearchComponent
  extends PagingComponent
  implements OnInit, OnDestroy, HasPageTitleAndMetaData, HasAnalytics
{
  environment: any; // Need this for template to provide base url to an external link (ex. docebo user profile)
  isShowFullCatalog = true; // Show Full Catalog Hero text and description by default
  isLoading: boolean;
  fragment: string;
  name: string; // slug name

  // Search Query
  query: string; // search params query
  charLimit = 50; // Search query character limit configuration

  // Sort options
  isSortAscending = false; // set isSortAscending to false since default results are not sorted

  subscriptionIds: number[] = environment.beyond.subscription_ids;

  // Search Mobile Filter Button
  disableApplyButton = true; // TODO: Fix me

  // Applied filters array - selected checked items
  // TODO: Refactor this out of here
  public roleFilterArray: [] = [];
  public productFilterArray: [] = [];
  public topicFilterArray: [] = [];
  public learningTypeFilterArray: [] = [];
  public courseAvailabilityFilterArray: [] = [];

  // Keep copy of CMS Subscriptions outside of Docebo search results
  subscriptionItems: LMSSubscription[] = [];

  // Accordion filter catalog items
  toggleFilter = false;
  filterCatalogItemsAccordionData: FilterCategoryItem[] = [];
  filterELearningItemsAccordionData: FilterCategoryItem[] = [];

  @ViewChild('searchCatalogAccordion', { static: true }) accordionComponent: ElementRef;

  myFilterElement = false; // TODO: Fix me - this handles the filter icon accordion toggle.

  currentUser: User;
  results: SearchObject[] = [];

  // Handle special case where courses can be in multiple subcriptions
  // Predictive Engagement: Altocloud, Decisions Administration, Decisions Strategic Planning
  courseList: number[] = environment.beyond.course_ids_subscription_list;

  subs: Subscription[] = []; // Subscribe to the search results
  toggleSwitch = false;

  roleBasedLearningPathContent: StaticPage;
  freeLearningContent: StaticPage;

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private searchService: SearchService,
    private enrollmentService: EnrollmentService,
    private subscriptionService: SubscriptionService,
    private filterCategoryFactory: FilterCategoryFactory,
    private wpSubscriptionService: WordpressSubscriptionsService,
    private subsAdapter: SubscriptionAdapter,
    private staticPageAdapter: StaticPageAdapter,
    private announcementService: AnnouncementService
  ) {
    super();
    // To trigger OnInit() when View Full Catalog button is clicked from Mega menu in Search page.
    route.params.subscribe((param) => {
      const isParamsEmpty =
        param.hasOwnProperty('type') ||
        param.hasOwnProperty('topic') ||
        param.hasOwnProperty('role') ||
        param.hasOwnProperty('product') ||
        param.hasOwnProperty('s');
      if (!isParamsEmpty) {
        this.resetFields();
        this.ngOnInit();
      }
    });
  }

  /**
   * OnInit - Set up user, environment, title and meta data.
   * Initialize the accordion fields for catalog searching and filtering
   * Initialize paging defaults
   * Initialize any incoming search emited from the search input or link
   */
  async ngOnInit(): Promise<void> {
    this.environment = environment;
    // NOTE: User service handles and knows the authentication service, subscription service,
    // course service and enrollment service. This keeps the session docebo token in sync
    // on refresh or reload.
    // NOTE: Do not use firstValueFrom if user is not signed in, it will silently fail.
    // Using the pipe, catchError and return of(null) with firstValueFrom will not work either.
    // This will handle both signed in and un-assigned users.
    // This fixes the catalog search by role, topic, and learning types.
    this.userService.doceboUser$.subscribe({
      next: (results: User) => {
        this.currentUser = results;
      },
      error: (err) => {
        console.log('undefined user', err);
      },
      complete: () => {},
    });
    this.setTitleAndMetaData();

    // Set Default Paging parameters
    this.itemsPerPage = 15;
    this.currentPage = 1;

    // Set Accordion check items to be loaded
    // Build the accordion check items and parent menus based
    this.filterCatalogItemsAccordionData = this.filterCategoryFactory.getCollection();

    this.resetFilterCatalogItems();
    this.getRoleBasedLearningPathContent();
    this.getFreeLearningContent();

    // Remove recommend courses in sessions storage if not empty
    if (sessionStorage.getItem(SessionStorageKeys.RECOMMENDED_FILTERED_SEARCH_RESULTS) != null) {
      sessionStorage.removeItem(SessionStorageKeys.RECOMMENDED_FILTERED_SEARCH_RESULTS);
    }

    // Load CMS Subscriptions
    this.getSubscriptions();

    // Handle search
    this.subs.push(
      this.route.params.subscribe((params: Params) => {
        // Load search query params for search
        this.setQuery(params);

        // Set current paging number
        this.setCurrentPage(params);

        if (params.s || params.cs) {
          // Clear Cache
          sessionStorage.removeItem(SessionStorageKeys.FILTERED_SEARCH_RESULTS);
        }

        // Use sessionStorage to store the full and filter results to cache.
        if (sessionStorage.getItem(SessionStorageKeys.FILTERED_SEARCH_RESULTS) == null) {
          if (sessionStorage.getItem(SessionStorageKeys.FULL_SEARCH_RESULTS) == null) {
            // Do Full Catalog Search if true
            if (params.product) {
              this.getSearchResultsByProduct(params.product);
            } else if (params.role) {
              this.fragment = params.role;
              this.getSearchResultsByRole(params.role);
            } else if (params.topic) {
              this.fragment = params.topic;
              this.getSearchResultsByTopic(params.topic);
            } else if (params.type) {
              this.fragment = params.type;
              this.getSearchResultsByLearningType(params.type);
            } else {
              this.search(true);
            }
          } else if (params.s) {
            // Else do a E-Learning Search if false
            this.search(false);
          } else if (params.product) {
            this.getSearchResultsByProduct(params.product);
          } else if (params.role) {
            this.fragment = params.role;
            this.getSearchResultsByRole(params.role);
          } else if (params.topic) {
            this.fragment = params.topic;
            this.getSearchResultsByTopic(params.topic);
          } else if (params.type) {
            this.fragment = params.type;
            this.getSearchResultsByLearningType(params.type);
          } else {
            this.isLoading = false;

            // Update and replace Docebo subscriptions with CMS Subscriptions
            this.updateFullSearchResults();
            const fullResults = JSON.parse(
              sessionStorage.getItem(SessionStorageKeys.FULL_SEARCH_RESULTS)
            );

            this.results = this.setParentSubscriptions();

            const now = new Date();
            // compare the expiry time of the item with the current time
            if (
              now.getTime() > fullResults &&
              fullResults.searchresults &&
              fullResults.searchresults.expiry
            ) {
              // If the item is expired, delete the item from storage
              sessionStorage.removeItem(SessionStorageKeys.FULL_SEARCH_RESULTS);
            }
          }
        } else if (params.product) {
          this.getSearchResultsByProduct(params.product);
        } else if (params.role) {
          this.fragment = params.role;
          this.getSearchResultsByRole(params.role);
        } else if (params.topic) {
          this.fragment = params.topic;
          this.getSearchResultsByTopic(params.topic);
        } else if (params.type) {
          this.fragment = params.type;
          this.getSearchResultsByLearningType(params.type);
        } else {
          // Load filtered search results from cache
          this.isLoading = false;
          // Update filtered search results to update parent subscriptions if course appears in more than
          // 1 or N subscriptions.
          this.results = this.setParentSubscriptions();
        }

        // this will trigger when we click on View elearning button from Dashboard Page
        // This will default the search to elearning page
        if (params && params.elearning) {
          this.toggleSwitch = true;
          this.toggleNavigation(true);
        }
      })
    );
  }

  /**
   * On Destroy, unsubscribe subject containing user
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  /**
   * Returns a truncated query string
   * @param query search params query string
   * @returns a truncated query by character limit size
   */
  getQuery(query): string {
    return truncate(query, this.charLimit);
  }

  /**
   * Read the params object query string and store it to the local query for search
   * @param params - router parameter object that contains the search query
   */
  setQuery(params: any): void {
    this.query = params.s || '';
  }

  /**
   * Update current search page based on selected page and update
   * search results.  Also scroll into view after page change.
   * @param page - set current page to selected page
   */
  pageChanged(page: number): void {
    if (this.hasPageChanged(page)) {
      this.currentPage = page;
      this.updateSearchURL();
    }
    // TODO: Move this into scrolling utilities
    document
      .querySelector('#contentWrapper')
      .scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Update search route url with additional route options to include the current query and current page.
   * Route options contains current query string and current page index.
   */
  updateSearchURL(): void {
    const routeOptions = {
      s: this.query,
      p: this.currentPage,
    };

    const url = this.router.createUrlTree([routeOptions], { relativeTo: this.route }).toString();
    this.location.go(url);
  }

  /**
   * Handle catalog search from the emited search query
   * @query - incoming event that has search query from search field input
   */
  onCatalogSearch(query: string): void {
    // Set query from event into local variable to search
    this.query = query;

    // Set current page, defaults to page 1
    this.setCurrentPage();

    // Update search url route and params
    this.updateSearchURL();

    // Run search
    this.search(false);
  }

  /**
   * Searches course catalog and filters based on role,
   * product, topic, and learning type
   * @param isFullSearch - boolean indicating if this is going to be a full search or filtered search
   */
  search(isFullSearch: boolean): void {
    this.isLoading = true;
    const options = this.getSearchOptions();
    this.getSearchResults(options, isFullSearch);
  }

  /**
   * Apply filters is another call to search where the isFullSearch is false
   */
  applyFilters(): void {
    this.search(false);
    this.setCurrentPage();
    this.updateSearchURL();
  }

  /**
   * Sort search results by item name or course or subscription name
   * Sort ascending will sort in ascending order.
   * Sort descending will sort in reverse or descending order.
   */
  sortByName(): void {
    // Split Subscriptions (Docebo has them as learning_plan) from the result since Subscriptions should not be sorted
    const subscriptions = this.results.filter(
      (result) => result.type === Learning_Type.SUBSCRIPTION
    );

    // Filter out courses only to sort
    this.results = this.results.filter((result) => result.type !== Learning_Type.SUBSCRIPTION);
    if (!this.isSortAscending) {
      this.results.sort((a, b) => a.name.localeCompare(b.name));
      this.isSortAscending = true;
    } else {
      this.results.reverse();
      this.isSortAscending = false;
    }
    // Re-add the subscriptions back to the search results
    this.results = subscriptions.concat(this.results);
  }

  /**
   * Return the current search options and query state
   * @returns - determine current state of query and options
   */
  getSearchOptions(): any {
    // Get the local reference to the search query
    const query = this.query ? this.query : '';
    // Get the accordion check items that are checked and send their array values to the search service
    let learningTypes = [];
    if (this.isShowFullCatalog) {
      learningTypes = this.learningTypeFilterArray.length > 0 ? this.learningTypeFilterArray : [];
      // Auto-Select Instructor-Led if Self-study is selected and vice-versa
      if (learningTypes.includes(Learning_Type.INSTRUCTOR_LED)) {
        sessionStorage.setItem(Learning_Type.SELF_STUDY, JSON.stringify(false));
        this.selectInstructionType({
          learningTypeArray: learningTypes,
          learningType: Learning_Type.SELF_STUDY,
        });
      } else if (learningTypes.includes(Learning_Type.SELF_STUDY)) {
        sessionStorage.setItem(Learning_Type.SELF_STUDY, JSON.stringify(true));
        this.selectInstructionType({
          learningTypeArray: learningTypes,
          learningType: Learning_Type.INSTRUCTOR_LED,
        });
      }
    } else {
      // Else set learning type to be 'eLearning'
      learningTypes = [Learning_Type.E_LEARNING];
    }
    const options = {
      query,
      roleFilterArray: this.roleFilterArray.length > 0 ? this.roleFilterArray : [],
      productFilterArray: this.productFilterArray.length > 0 ? this.productFilterArray : [],
      topicFilterArray: this.topicFilterArray.length > 0 ? this.topicFilterArray : [],
      learningTypeArray: learningTypes,
      courseAvailabilityFilterArray:
        this.courseAvailabilityFilterArray.length > 0 ? this.courseAvailabilityFilterArray : [],
    };
    return options;
  }

  /**
   * Select Instruction Type for Instructor-Led and Self-Study, if either is selected.
   * Returns search results that combines both if they having related course ids
   * @param learningTypeArray - Learning Type array has selected Instruction Type filter
   * @param learningType params has either Instructor-Led or Self-Study
   */
  selectInstructionType({
    learningTypeArray,
    learningType,
  }: {
    learningTypeArray: any;
    learningType: string;
  }): void {
    if (!learningTypeArray.includes(learningType)) {
      learningTypeArray.push(learningType);
    }
    // NOTE: If we allow the auto-select to check the instruction types, uncomment this out in future.
    // const instructionTypeArray = this.filterCatalogItemsAccordionData[1];
    // instructionTypeArray.array.forEach((item) => {
    //   if (item.value === learningType) {
    //     item.checked = true;
    //   }
    // });
  }

  /**
   * Exclude search results in the search object map based on instruction type filter to include in the search and
   * also the learning type to be excluded or removed from the seach objects results map
   * @param searchObjectsMap - Search objects response to be filtered
   * @param instructionTypeArray - Instruction type filter category item
   */
  excludeCoursesFromSearch(
    searchObjectsMap: Map<string, SearchObject>,
    instructionTypeArray: FilterCategoryItem
  ): void {
    // Check if you are filtering by Self-Study, Instructor-Led or Both.
    // If you are filtering by Self-Study, then EXCLUDE any courses that are just Instructor-Led only (no matching pair or no matching related coruse)
    const selectedSelfStudyIndex = instructionTypeArray.array.findIndex(
      (item) => item.value === Learning_Type.SELF_STUDY && item.checked === true
    );
    const selectedInstructorLedIndex = instructionTypeArray.array.findIndex(
      (item) => item.value === Learning_Type.INSTRUCTOR_LED && item.checked === true
    );
    const isSelfStudyOnly = selectedInstructorLedIndex === -1 && selectedSelfStudyIndex !== -1;
    const isInstructorLedOnly = selectedInstructorLedIndex !== -1 && selectedSelfStudyIndex === -1;

    const orphanedCourses = [];

    // Handle Case 1 where Self-study is checked only and Instructor-Led is not checked
    // Exclude courses that just have Instructor-Led and unmatched related course id
    // Handle Case 2 where Self-study is unchecked only and Instructor-Led is checked
    // Exclude courses that just have Self-Study and unmatched related course id
    // Handle Case 3 is if both are checked, then return everything back if the orphaned courses is empty.

    searchObjectsMap.forEach((item) => {
      if (
        (isSelfStudyOnly &&
          item.relatedCourseIds &&
          item.relatedCourseIds.length === 0 &&
          item.instructionType === Learning_Type.INSTRUCTOR_LED) ||
        (isInstructorLedOnly &&
          item.relatedCourseIds &&
          item.relatedCourseIds.length === 0 &&
          item.instructionType === Learning_Type.SELF_STUDY)
      ) {
        orphanedCourses.push(item.code);
      }
    });

    // Else remove the orphaned courses from the search objects map
    if (orphanedCourses && orphanedCourses.length > 0) {
      orphanedCourses.forEach((itemCode) => {
        searchObjectsMap.delete(itemCode);
      });
    }
  }

  /**
   * Pull the Query data
   * @param query returns query
   */
  onChangeQuery(query): void {
    this.query = query;
  }

  /**
   * Return boolean current date time is greater than the expiration time set.
   * @param expiredTime - expired time set by getExpirationTime that is cached with search results.
   * @returns boolean true current time is greater than the set expired time, esle return false.
   */
  hasExpired(expiredTime: number): boolean {
    const now = new Date();
    return now.getTime() > expiredTime;
  }

  /**
   * Filter search results by product name
   * @param product - product params to search results by product name
   */
  getSearchResultsByProduct(product: string): void {
    this.resetFilterCatalogItems();
    this.isLoading = false;
    // Check the product accordingly based on the params.product value
    const genesysProduct = this.filterCatalogItemsAccordionData[0];
    genesysProduct.isExpanded = true;
    genesysProduct.array.forEach((item) => {
      item.checked = false;
      if (item.value === product) {
        item.checked = true;
      }
      if (product === Product.GENESYS_ENGAGE) {
        if (item.value === Product.GENESYS_ENGAGE_CLOUD) {
          item.checked = true;
        }
      }
    });

    const productArray = [];
    if (product === Product.GENESYS_ENGAGE) {
      productArray.push(product, Product.GENESYS_ENGAGE_CLOUD);
    } else {
      productArray.push(product);
    }

    // NOTE: Else we filter the catalog by subscription from the subscription detail page if the user
    // selects the "View All Courses or Explore Catalog button".
    const options = {
      query: '',
      roleFilterArray: [],
      productFilterArray: productArray,
      topicFilterArray: [],
      learningTypeArray: [],
      courseAvailabilityFilterArray: [],
    };

    this.getSearchResults(options, false);
  }

  /**
   * Filter search results by role name
   * @param role - role params to search results by role name
   */
  getSearchResultsByRole(role: string): void {
    this.resetFilterCatalogItems();
    this.isLoading = true;
    // Check the product accordingly based on the params.role value
    const genesysRole = this.filterCatalogItemsAccordionData[2];
    genesysRole.isExpanded = true;
    genesysRole.array.forEach((item) => {
      item.checked = false;
      if (item.value === role) {
        item.checked = true;
      }
    });

    const roleArray = [];
    roleArray.push(role);

    // NOTE: Else we filter the catalog by subscription from the subscription detail page if the user
    // selects the "View All Courses or Explore Catalog button".
    const options = {
      query: '',
      roleFilterArray: roleArray,
      productFilterArray: [],
      topicFilterArray: [],
      learningTypeArray: [],
      courseAvailabilityFilterArray: [],
    };

    this.getSearchResults(options, false);
  }

  /**
   * Filter search results by topic name
   * @param topic - topic params to search results by topic name
   */
  getSearchResultsByTopic(topic: string): void {
    this.resetFilterCatalogItems();
    this.isLoading = false;
    // Check the product accordingly based on the params.topic value
    const genesysTopic = this.filterCatalogItemsAccordionData[3];
    genesysTopic.isExpanded = true;
    genesysTopic.array.forEach((item) => {
      item.checked = false;
      if (item.value === topic) {
        item.checked = true;
      }
    });

    const topicArray = [];
    topicArray.push(topic);

    // NOTE: Else we filter the catalog by subscription from the subscription detail page if the user
    // selects the "View All Courses or Explore Catalog button".
    const options = {
      query: '',
      roleFilterArray: [],
      productFilterArray: [],
      topicFilterArray: topicArray,
      learningTypeArray: [],
      courseAvailabilityFilterArray: [],
    };

    this.getSearchResults(options, false);
  }

  /**
   * Filter search results by topic name
   * @param topic - topic params to search results by topic name
   */
  getSearchResultsByLearningType(learning_type: string): void {
    this.resetFilterCatalogItems();
    this.isLoading = true;
    // Check the product accordingly based on the params.topic value
    const genesysLearningType = this.filterCatalogItemsAccordionData[1];
    genesysLearningType.isExpanded = true;
    genesysLearningType.array.forEach((item) => {
      item.checked = false;
      if (item.value === learning_type) {
        item.checked = true;
      }
    });

    const learningTypeArray = [];
    learningTypeArray.push(learning_type);

    // NOTE: Else we filter the catalog by subscription from the subscription detail page if the user
    // selects the "View All Courses or Explore Catalog button".
    const options = {
      query: '',
      roleFilterArray: [],
      productFilterArray: [],
      topicFilterArray: [],
      learningTypeArray: learningTypeArray,
      courseAvailabilityFilterArray: [],
    };

    this.getSearchResults(options, false);
  }

  /**
   * Return search results based on search query and filter checked items
   * @param options - send in checked filtered items selected or unselected
   * @param isFullSearch - boolean to indicate a full search results (true) or filtered search results (false)
   */
  getSearchResults(options: any, isFullSearch: boolean): void {
    // Send Google Analytics on options query
    // TODO: Pass in filter checked items into query for Google Analytics
    this.sendGoogleAnalytics('Search', 'Query', options.query);

    const searchObjectsMap = new Map();

    // Check if full search is true. If full search is true or the default search when all the filters are empty
    // TODO: Refactor this method. It's the same in course-detail.component.ts
    this.searchService
      .getSearchCourses(options)
      .then((response: any) => {
        // Set expiration time for search results
        // Reduce or consolidate duplicate search object courses to appear as one
        // course.
        let sortedResponse = [];
        const subscriptions = response.filter(
          (result) => result.type === Learning_Type.SUBSCRIPTION
        );
        subscriptions.forEach((subscription) => {
          // Replace each subscription with CMS Subscription
          const cmsSubscriptionSearchObject = JSON.parse(sessionStorage.getItem(subscription.slug));
          if (cmsSubscriptionSearchObject) {
            const matchingSubscriptionIndex = response.findIndex(
              (item) => item.code === cmsSubscriptionSearchObject.code
            );
            response[matchingSubscriptionIndex] = cmsSubscriptionSearchObject;
            if (!searchObjectsMap.has(cmsSubscriptionSearchObject.code)) {
              searchObjectsMap.set(cmsSubscriptionSearchObject.code, cmsSubscriptionSearchObject);
            }
          }
        });

        // Filter out courses only to sort
        sortedResponse = response.filter((result) => result.type !== Learning_Type.SUBSCRIPTION);
        sortedResponse.forEach((item) => {
          // get the current item's code, store it a map
          if (!searchObjectsMap.has(item.code)) {
            searchObjectsMap.set(item.code, item);
          } else {
            // If the key exists, get the last course object and update
            // NOTE: Handle Self-study and ILT courses. They keys or the course names
            // are not necessarily in alphabetical order. If the previous code is self study
            // then you need to swap that map key with the one that is alpabetically in the correct order
            // and swap the keep and default object
            // Compare previous and incoming items by course name, specifically for Self-Study and Instructor Led (ILT)
            // Data can be pre-sorted outside of UI (i.e. in Service, Docebo, etc...) or user can click sort descending, so you must
            // compare each time
            compareSwap(searchObjectsMap, item);
          }
        });

        // Remove any orphaned or single courses that don't match the search criteria above.
        // If there is only Instructor-Led for Self-Study search, remove it from search results
        // If there is only Self-Study for a Instructor-Led search, remove it from search results.
        this.excludeCoursesFromSearch(searchObjectsMap, this.filterCatalogItemsAccordionData[1]);

        // Convert map into array of values with distinct search objects, which has removed
        // the duplicate search objects
        this.results = Array.from(searchObjectsMap.values()) || [];

        // Set parent subscriptions to see if courses can have multiple subscriptions
        if (this.results && this.results.length > 0) {
          this.results = this.setParentSubscriptions();
        }

        const searchResultsItem = {
          searchresults: this.results,
          expiry: getExpirationTime(), // current time + 12 hours
        };

        // Reduce search course results that have the same code
        // Update courses to remember sibling courses with the same code.
        if (isFullSearch) {
          this.updateFullSearchResults();
          sessionStorage.setItem(
            SessionStorageKeys.FULL_SEARCH_RESULTS,
            JSON.stringify(searchResultsItem)
          );
        } else {
          sessionStorage.setItem(
            SessionStorageKeys.FILTERED_SEARCH_RESULTS,
            JSON.stringify(this.results)
          );
        }
      })
      .catch((err) => {
        console.log('unable to get search results, ', err);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  /**
   *
   * Toggle the accordion to show or hide
   */
  onToggleFilter(): void {
    this.toggleFilter = !this.toggleFilter;
  }

  /**
   * Reset filter catalog items data and reset all checked items to be false or unchecked.
   */
  resetFilterCatalogItems(): void {
    this.filterCatalogItemsAccordionData = this.filterCategoryFactory.getCollection();
    // duplicate code. accordion should do this not search component
    this.filterCatalogItemsAccordionData.forEach((accordiondata) => {
      accordiondata.isExpanded = false;
      accordiondata.array.forEach((item) => {
        item.checked = false;
      });
    });
    this.setCurrentPage();
  }

  /**
   * Reset catalog according catalog items
   */
  resetFields(): void {
    this.disableApplyButton = true;

    // Reset accordion catalog filter items
    this.resetFilterCatalogItems();

    this.roleFilterArray = [];
    this.productFilterArray = [];
    this.topicFilterArray = [];
    this.learningTypeFilterArray = [];
    this.courseAvailabilityFilterArray = [];

    this.query = '';
    // NOTE: If it is Elearning, reset learning type array to Elearning

    const options = {
      query: this.query,
      roleFilterArray: this.roleFilterArray,
      productFilterArray: this.productFilterArray,
      topicFilterArray: this.topicFilterArray,
      learningTypeArray: this.isShowFullCatalog
        ? this.learningTypeFilterArray
        : [Learning_Type.E_LEARNING],
      courseAvailabilityFilterArray: this.courseAvailabilityFilterArray,
    };
    sessionStorage.removeItem(SessionStorageKeys.FILTERED_SEARCH_RESULTS);

    if (sessionStorage.getItem(SessionStorageKeys.FULL_SEARCH_RESULTS) == null) {
      this.isLoading = true;
      this.getSearchResults(options, false);
    } else {
      // Update and replace Docebo subscriptions with CMS Subscriptions
      this.updateFullSearchResults();
      const fullResults = JSON.parse(
        sessionStorage.getItem(SessionStorageKeys.FULL_SEARCH_RESULTS)
      );
      this.results = fullResults.searchresults;
    }
    this.results = this.results.filter((result) => result.type !== 'learning_plan');
    this.updateSearchURL();
  }

  /**
   * TODO: Refactor this to accordion or outside component.
   * Handle accordion filter selected checked items from emitted event from accordion.
   * @param selected emitted event from accordion indicating what items are checked or unchecked.
   */
  selectedItems(selected: any): void {
    if (selected.match === FilterMatchType.GENESYS_PRODUCT) {
      if (selected.selectedCheckBox.length > 0) {
        this.productFilterArray = selected.selectedCheckBox;
        this.disableApplyButton = false;
      } else {
        this.productFilterArray = [];
      }
    }

    if (selected.match === FilterMatchType.INSTRUCTION_TYPE) {
      if (selected.selectedCheckBox.length > 0) {
        this.learningTypeFilterArray = selected.selectedCheckBox;
        this.disableApplyButton = false;
      } else {
        this.learningTypeFilterArray = [];
      }
    }

    if (selected.match === FilterMatchType.ROLE) {
      if (selected.selectedCheckBox.length > 0) {
        this.roleFilterArray = selected.selectedCheckBox;
        this.disableApplyButton = false;
      } else {
        this.roleFilterArray = [];
      }
    }

    if (selected.match === FilterMatchType.TOPIC) {
      if (selected.selectedCheckBox.length > 0) {
        this.topicFilterArray = selected.selectedCheckBox;
        this.disableApplyButton = false;
      } else {
        this.topicFilterArray = [];
      }
    }

    if (selected.match === FilterMatchType.COURSE_AVAILABILITY) {
      if (selected.selectedCheckBox.length > 0) {
        this.courseAvailabilityFilterArray = selected.selectedCheckBox;
        this.disableApplyButton = false;
      } else {
        this.courseAvailabilityFilterArray = [];
      }
    }

    if (
      this.productFilterArray.length === 0 &&
      this.learningTypeFilterArray.length === 0 &&
      this.roleFilterArray.length === 0 &&
      this.topicFilterArray.length === 0 &&
      this.courseAvailabilityFilterArray.length === 0
    ) {
      this.disableApplyButton = true;
    }
  }

  /**
   * Handle the emitted event from the toggle switch
   * This updates the hero results text to show either full catalog or elearning content
   * @param isToggleOn incoming emitted event from toggle switch
   */
  toggleNavigation(isToggleOn: boolean): void {
    // Update hero content text based on emitted event
    this.isShowFullCatalog = !isToggleOn;

    // Update title and meta description on toggle switch
    this.setTitleAndMetaData();

    if (this.isShowFullCatalog) {
      // We are filtering and showing full catalog results
      this.filterCatalogItemsAccordionData = this.filterCategoryFactory.getCollection();
      // Call search
      this.search(true);
    } else {
      // Else we are showing filtered eLearning search results
      // Call search
      this.search(false);
      this.filterELearningItemsAccordionData = [];
      // Reset any filter catalog items when switching to eLearning toggle switch filter
      this.resetFilterCatalogItems();
      this.filterCatalogItemsAccordionData.forEach((item) => {
        if (item.title !== FilterMatchTitle.INSTRUCTION_TYPE) {
          this.filterELearningItemsAccordionData.push(item);
        }
      });
      this.filterCatalogItemsAccordionData = this.filterELearningItemsAccordionData;
    }

    // TODO: Make this a function. Should we combine search results and elearning as the same thing?
    sessionStorage.removeItem(SessionStorageKeys.FILTERED_SEARCH_RESULTS);
  }

  /**
   * Return a course query object from the course result
   * @param courseIds - course id number
   * @param courseSlug - course slug string
   * @returns - a object containing the course id
   */
  getCourseQuery(courseId: number, courseSlug: string): any {
    let courseObject = {};
    if (courseId) {
      courseObject = { id: courseId, slug: courseSlug };
      sessionStorage.setItem(courseSlug, String(courseId));
    }
    return courseObject;
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics(category, action, dataLabel);
    this.googleAnalyticsService.sendEvent(category, action, dataLabel);
  }

  /**
   * Open Request a Voucher Modal for Certification Exams
   * Assume that current user is logged in and the instruction type is
   * Certification Exam, else ignore event.
   * @param clickEvent - handle event emitted from the search result card component
   * @param instructionType - course instruction type whic needs to be certification exam
   * @param course - selected Certification Exam to be processed and formatted for email notification.
   */
  openModal(clickEvent: any, instructionType: string, course: SearchObject): void {
    if (this.currentUser && clickEvent && clickEvent.isClicked) {
      // TODO: OPen modal
      if (course && instructionType === Learning_Type.CERTIFICATION_EXAM) {
        if (document.getElementById('requestForm')) {
          document.getElementById('requestForm').style.display = 'block';
          // NOTE: Fix the background body to be fixed to prevent background scrolling when modal is open
          lockBackgroundScrolling();
          // Pass in course model to modal popup via session storage
          sessionStorage.removeItem(SessionStorageKeys.REQUEST_VOUCHER);
          sessionStorage.setItem(SessionStorageKeys.REQUEST_VOUCHER, JSON.stringify(course));
        }
      }
    }
  }

  /**
   * Set selected subscription title and metadata tags
   */
  setTitleAndMetaData(): void {
    if (this.isShowFullCatalog) {
      // NOTE: Update page title if ths show full catalog is toggled
      this.titleService.setTitle('Find the right training for you | Genesys Education');
      // NOTE: Update meta tag description for page
      this.metaService.updateTag({
        name: 'description',
        content: 'Explore the Genesys Education catalog and find the right training for you.',
      });
    } else {
      // NOTE:  Update page title if ths show eLearning is toggled
      this.titleService.setTitle('eLearning | Genesys Education');
      // NOTE: Update meta tag description for page
      this.metaService.updateTag({
        name: 'description',
        content:
          'Explore the Genesys Education eLearning catalog and find the right training for you.',
      });
    }
  }

  /**
   * Assign parent subscriptions for courses that can have 1 or more subscriptions associated to it.
   */
  setParentSubscriptions(): SearchObject[] {
    let subscriptionsList = [];
    let isFullSearch = false;
    const filteredResults = JSON.parse(
      sessionStorage.getItem(SessionStorageKeys.FILTERED_SEARCH_RESULTS)
    );
    const fullResults = JSON.parse(sessionStorage.getItem(SessionStorageKeys.FULL_SEARCH_RESULTS));

    if (this.results && this.results.length === 0) {
      // If the current search results is empty, then check filtered search results
      isFullSearch = false;

      if (filteredResults && filteredResults.length > 0) {
        this.results = filteredResults;
      } else {
        // Else check full search results if current results is empty
        if (fullResults && fullResults.searchresults && fullResults.searchresults.length > 0) {
          isFullSearch = true;
          this.results = fullResults.searchresults;
        }
      }
    }

    this.subscriptionService.getSubscriptions(this.subscriptionIds).subscribe({
      next: (subscriptionItems) => {
        subscriptionsList = subscriptionItems.filter((nonulls) => nonulls);
        subscriptionsList.forEach((subscriptionItem) => {
          // TODO: Refactor nested for loops
          // To Improve performance
          subscriptionItem.data.courses.forEach((item) => {
            // NOTE: TODO: Refactor the course_ids_subscription_list to be more dynamic and not read from a file.
            // This file handles multiple subscriptions. Need to maintain if the multiple subscription changes.
            if (item.idCourse || this.courseList.includes(item.idCourse)) {
              this.results.forEach((course) => {
                if (item.idCourse === course.id) {
                  if (!course.parentSubscriptionIds.includes(subscriptionItem.data.id)) {
                    course.parentSubscriptionIds.push(subscriptionItem.data.id);
                  }
                }
              });
            }
          });
        });
      },
      error: (error) => {
        this.isLoading = false;
        console.log(error);
      },
      complete: () => {
        // Force is loading to stop and complete in case of any unhandled errors
        this.isLoading = false;
        console.log('Search Parent Subscriptions content completed');
      },
    });
    this.results = this.results.filter((result) => result.type !== 'learning_plan');
    return this.results;
  }

  /**
   * Subscribe and return list of subscriptions
   */
  getSubscriptions(): void {
    // Default word press CMS
    this.isLoading = true;
    this.subs.push(
      this.wpSubscriptionService.getSubscriptionList().subscribe({
        next: (subscriptionItems: CMSSubscriptionInterface[]) => {
          this.isLoading = false;

          subscriptionItems.forEach((item: CMSSubscriptionInterface) => {
            const subscriptionItem = this.subsAdapter.adapt(item);
            if (subscriptionItem) {
              this.subscriptionItems.push(subscriptionItem);
              // Create CMS Subscription replacement Search Object
              this.createCMSSubscriptionSearchObject(subscriptionItem);
            }
          });
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Search Subscriptions content completed');
        },
      })
    );

    // Create CMS Subscription replacement Search Object
    this.subscriptionItems.forEach((item) => {
      if (item) {
        this.subscriptionItems.push(item);
        this.createCMSSubscriptionSearchObject(item);
      }
    });
  }

  /**
   * Create CMS Subscription Search Objects to replace Docebo descriptions from CMS
   * @param subscriptionItem - subscription item that is used to create the CMS Subscription Search object
   */
  createCMSSubscriptionSearchObject(subscriptionItem: LMSSubscription): void {
    if (subscriptionItem) {
      // Adapt LMS Subscription into Search Object
      // Do not cache any invalid subscriptions with ids of -1, ex BeyondCX.
      // If we ever want to expose BeyondCX or any non-Docebo subscription in search that have to be added to the catalog search
      // in a future request.
      const subscriptionSearchObject = new SearchObject(
        -1,
        '',
        subscriptionItem.id,
        subscriptionItem.name,
        subscriptionItem.code,
        subscriptionItem.description, // This is the description we want to replace in the search results for docebo subscriptions
        0,
        0,
        Learning_Type.SUBSCRIPTION,
        subscriptionItem.slugName,
        '',
        [],
        []
      );
      sessionStorage.setItem(
        subscriptionSearchObject.slug,
        JSON.stringify(subscriptionSearchObject)
      );
    }
  }

  /**
   * Update Full Search Results Subscriptions to use CMS Subscription content instead of Docebo descriptions and content
   */
  updateFullSearchResults(): void {
    const fullResults = JSON.parse(sessionStorage.getItem(SessionStorageKeys.FULL_SEARCH_RESULTS));
    // Update and replace Docebo subscriptions with CMS Subscriptions

    if (fullResults && fullResults.searchresults) {
      // Split Subscriptions (Docebo has them as learning_plan) from the result since Subscriptions should not be sorted
      const subscriptions = fullResults.searchresults.filter(
        (result) => result.type === Learning_Type.SUBSCRIPTION
      );
      if (subscriptions) {
        subscriptions.forEach((subscription) => {
          // Replace each subscription with CMS Subscription
          const cmsSubscriptionSearchObject = JSON.parse(sessionStorage.getItem(subscription.slug));
          if (cmsSubscriptionSearchObject) {
            const matchingIndex = fullResults.searchresults.findIndex(
              (item) => item.code === cmsSubscriptionSearchObject.code
            );
            fullResults.searchresults[matchingIndex] = cmsSubscriptionSearchObject;
          }
        });
        sessionStorage.setItem(SessionStorageKeys.FULL_SEARCH_RESULTS, JSON.stringify(fullResults));
      }
    }
  }

  /**
   * Enroll and update Certification course from Enroll to Start state directly
   * Default session id is set to 0, since eLearning does not have a session id.
   * This will enroll the course and will also trigger an API to get the voucher for registering an certification exam through email.
   * @param clickEvent - handle event emitted from the search result card component
   * @param instructionType - course instruction type must be certification exam
   * @param courseId - required courseId for a given certification exam course
   * @param courseName - required course name for a given certificaton exam course
   */
  loadEnrollCertification(
    clickEvent: any,
    instructionType: string,
    courseId: number,
    courseName: string
  ) {
    // NOTE: Do not auto enroll users if they click on an internal route that is a link (link title or link description)
    // Load enroll should happen only on route types INTERNAL and from a button route type
    // NOTE: The links click type are string objects, but if you click on the card CTA button it's actually
    // the event object from the route button and object hence you need to check the typeof clickEvent.clickType
    if (
      this.currentUser &&
      clickEvent &&
      clickEvent.isClicked &&
      instructionType &&
      ((clickEvent.clickType &&
        typeof clickEvent.clickType === 'string' &&
        clickEvent.clickType !== RouteTypes.LINK) ||
        (clickEvent.clickType &&
          typeof clickEvent.clickType === 'object' &&
          clickEvent.clickType.clickType !== RouteTypes.LINK &&
          clickEvent.clickType.actionLabel &&
          clickEvent.clickType.actionLabel !== CardTypeCallToActions.REQUEST_A_QUOTE)) &&
      instructionType === Learning_Type.CERTIFICATION_EXAM
    ) {
      this.enrollmentService.enrollCourse(this.currentUser.id, courseId).then((response: any) => {
        if (
          response &&
          response.data &&
          response.data.enrolled &&
          response.data.enrolled.length > 0
        ) {
          this.enrollmentService
            .createCertificationVoucherForUser(this.currentUser, courseId, courseName)
            .then((response: any) => {
              if (response) {
                console.log('Final response from certification Voucher catalog page', response);
              }
            });
          this.userService.loadEnrollments(this.currentUser);
        }
      });
    }
  }

  /* Enroll and update eLearning course from Enroll to Start state directly
   * Default session id is set to 0, since eLearning does not have a session id.
   * @param clickEvent - handle event emitted from the search result card component
   * @param instructionType - course instruction type must be eLearning
   * @param courseId - pass the courseId for a given course, in this case an eLearning course
   */
  loadEnrollELearning(clickEvent: any, instructionType: string, courseId: number): void {
    // NOTE: Do not auto enroll users if they click on an internal route that is a link (link title or link description)
    // Load enroll should happen only on route types INTERNAL and from a button route type
    // NOTE: The links click type are string objects, but if you click on the card CTA button it's actually
    // the event object from the route button and object hence you need to check the typeof clickEvent.clickType
    if (
      this.currentUser &&
      clickEvent &&
      clickEvent.isClicked &&
      instructionType &&
      ((clickEvent.clickType &&
        typeof clickEvent.clickType === 'string' &&
        clickEvent.clickType !== RouteTypes.LINK) ||
        (clickEvent.clickType &&
          typeof clickEvent.clickType === 'object' &&
          clickEvent.clickType.clickType !== RouteTypes.LINK &&
          clickEvent.clickType.actionLabel &&
          clickEvent.clickType.actionLabel !== CardTypeCallToActions.REQUEST_A_QUOTE)) &&
      (instructionType === Learning_Type.E_LEARNING ||
        instructionType === Learning_Type.WEBINAR ||
        instructionType === Learning_Type.WORKSHOP)
    ) {
      this.enrollmentService
        .enrollCourse(this.currentUser.id, courseId)
        .then((response: any) => {
          if (
            response &&
            response.data &&
            response.data.enrolled &&
            response.data.enrolled.length > 0
          ) {
            this.userService.loadEnrollments(this.currentUser);
          } else {
            // Show enrollment errors in UI if any
            const errorObj = { errorMessage: response.data.errors[0] };
          }
        })
        .catch((error) => {
          const errorObj = { errorMessage: error.message };
        });
    }
  }

  /**
   * Get the Role based Learning Path content.
   */
  getRoleBasedLearningPathContent(): void {
    this.isLoading = true;
    const wordpressSlugName = AnnouncementSlugs.NEW_ROLE_BASED_LEARNING_PATHS_SLUG;
    this.subs.push(
      this.announcementService.getAnnouncementContentBySlug(wordpressSlugName).subscribe({
        next: (response: StaticPageInterface[]) => {
          if (response && response.length > 0) {
            this.roleBasedLearningPathContent = this.staticPageAdapter.adapt(response[0]);
            console.log('this.roleBasedLearningPathContent', this.roleBasedLearningPathContent);
          } else {
            console.log(`No announcements found for ${wordpressSlugName}!`);
          }
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Role based Learning Path content completed');
        },
      })
    );
  }

  /**
   * Get the Free Learning content.
   */
  getFreeLearningContent(): void {
    this.isLoading = true;
    const wordpressSlugName = AnnouncementSlugs.JUST_ADDED_MORE_FREE_LEARNING_CONTENT_SLUG;
    this.subs.push(
      this.announcementService.getAnnouncementContentBySlug(wordpressSlugName).subscribe({
        next: (response: StaticPageInterface[]) => {
          if (response && response.length > 0) {
            this.freeLearningContent = this.staticPageAdapter.adapt(response[0]);
          } else {
            console.log(`No announcements found for ${wordpressSlugName}!`);
          }
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Free Learning Content completed');
        },
      })
    );
  }

  /**
   * Use this function to reset your search results and bring back Full search results
   */
  clearSearchResults(): any {
    return { cs: true };
  }

  /**
   * Learning Type enums based on Learning types
   */
  public get learningType(): typeof Learning_Type {
    return Learning_Type;
  }

  /**
   *  CardTypeCallToActions enums based on card types
   */
  public get cardTypeCallToActions(): typeof CardTypeCallToActions {
    return CardTypeCallToActions;
  }

  /**
   * Add Track By Result Id to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of search object array
   * @param item - Search item
   * @returns result id number
   */
  trackByResultId(index: number, item: SearchObject): number {
    if (item) {
      return item.id;
    } else {
      return index;
    }
  }

  /**
   *  RouteTypes enums based on route types
   */
  public get routeTypes(): typeof RouteTypes {
    return RouteTypes;
  }
}
